import React, { useState, useEffect } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    useTheme,
    useMediaQuery,
    Typography,
    CircularProgress,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    SelectChangeEvent,
    styled,
    Grid,
    Card,
    CardContent,
    Skeleton,
    Chip,
    Divider,
    Avatar,
    Badge,
    Stack,
    Tooltip,
    IconButton,
    LinearProgress
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../contexts/AuthContext';
import { format, isBefore, isToday, addDays } from 'date-fns';
import { tr } from 'date-fns/locale';
import { OdevlerimError } from '../../../types/ErrorModals/OdevlerimError';
import { getOdevlerim } from '../../../services/api/odevlerimService';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import PersonIcon from '@mui/icons-material/Person';
import SchoolIcon from '@mui/icons-material/School';
import SubjectIcon from '@mui/icons-material/Subject';
import GradeIcon from '@mui/icons-material/Grade';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DescriptionIcon from '@mui/icons-material/Description';
import FilterListIcon from '@mui/icons-material/FilterList';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DoneIcon from '@mui/icons-material/Done';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningIcon from '@mui/icons-material/Warning';

// Styled components
const SeasonSelector = styled(FormControl)(({ theme }) => ({
    marginBottom: theme.spacing(3),
    marginTop: theme.spacing(2),
    '& .MuiOutlinedInput-root': {
        borderRadius: theme.shape.borderRadius * 1.5,
        '&.Mui-focused fieldset': {
            borderColor: theme.palette.primary.main,
            borderWidth: 2,
        },
    },
    '& .MuiInputLabel-root': {
        '&.Mui-focused': {
            color: theme.palette.primary.main,
        },
    },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    fontWeight: 'bold',
    padding: theme.spacing(1.5),
    [theme.breakpoints.down('md')]: {
        padding: theme.spacing(1),
    },
}));

const TextOverflowTableCell = styled(TableCell)(({ theme }) => ({
    padding: theme.spacing(1),
    maxWidth: 150,
    wordWrap: 'break-word',
    [theme.breakpoints.down('md')]: {
        padding: theme.spacing(0.75),
        maxWidth: 100,
    },
}));

const HeaderTableCell = styled(TableCell)(({ theme }) => ({
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fontWeight: 'bold',
    padding: theme.spacing(1.5),
    [theme.breakpoints.down('md')]: {
        padding: theme.spacing(1),
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    '&:hover': {
        backgroundColor: theme.palette.primary.light + '20',
        transition: 'background-color 0.3s ease',
    },
    transition: 'background-color 0.2s',
}));

const HomeworkCard = styled(Card)(({ theme }) => ({
    height: '100%',
    borderRadius: theme.shape.borderRadius * 1.5,
    boxShadow: theme.shadows[3],
    transition: 'transform 0.3s, box-shadow 0.3s',
    overflow: 'hidden',
    '&:hover': {
        transform: 'translateY(-5px)',
        boxShadow: theme.shadows[8],
    },
}));

const DeadlineChip = styled(Chip)(({ theme, status }: { theme: any, status: 'active' | 'due' | 'overdue' }) => {
    const colors = {
        active: theme.palette.success.main,
        due: theme.palette.warning.main,
        overdue: theme.palette.error.main
    };

    const icons = {
        active: <DoneIcon />,
        due: <WarningIcon />,
        overdue: <ErrorOutlineIcon />
    };

    return {
        fontWeight: 'bold',
        color: 'white',
        backgroundColor: colors[status],
        '& .MuiChip-icon': {
            color: 'white',
        },
        icon: icons[status]
    };
});

// Truncated text component for table cells
const TruncatedText = ({ text, maxLength = 30 }: { text: string, maxLength?: number }) => {
    const theme = useTheme();
    const displayText = text || '-';
    return (
        <Tooltip title={displayText} arrow placement="top">
            <Typography
                variant="body2"
                sx={{
                    display: 'block',
                    maxWidth: '100%',
                    fontSize: '0.75rem',
                    whiteSpace: 'normal',
                    wordBreak: 'break-word'
                }}
            >
                {displayText}
            </Typography>
        </Tooltip>
    );
};

const Odevlerim: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isTablet = useMediaQuery(theme.breakpoints.down("md"));
    const { kullaniciModal } = useAuth();

    const [selectedSeason, setSelectedSeason] = useState<string>('2025');
    const availableSeasons = ['2023', '2024', '2025', '2026', '2027'];

    // Add filter state
    const [statusFilter, setStatusFilter] = useState<string | null>(null);

    const handleSeasonChange = (event: SelectChangeEvent) => {
        setSelectedSeason(event.target.value);
    };

    const { data, isLoading, error, refetch } = useQuery({
        queryKey: ['odevlerim', kullaniciModal?.id, selectedSeason],
        queryFn: async () => {
            try {
                return await getOdevlerim({
                    kurumKodu: localStorage.getItem('kurumKodu') || '',
                    ogrenciId: kullaniciModal?.id || '',
                    sezon: parseInt(selectedSeason) + 1
                });
            } catch (err) {
                const odevlerimError = err as OdevlerimError;
                throw new Error(odevlerimError.message);
            }
        },
        enabled: !!kullaniciModal?.id,
        retry: 1
    });

    useEffect(() => {
        if (kullaniciModal?.id) {
            refetch();
        }
    }, [selectedSeason, refetch, kullaniciModal?.id]);

    // Get homework status
    const getHomeworkStatus = (teslimTarihi: Date): 'active' | 'due' | 'overdue' => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const dueDate = new Date(teslimTarihi);
        dueDate.setHours(0, 0, 0, 0);

        const tomorrow = addDays(today, 1);

        if (isBefore(dueDate, today)) {
            return 'overdue';
        } else if (isToday(dueDate) || dueDate.getTime() === tomorrow.getTime()) {
            return 'due';
        } else {
            return 'active';
        }
    };

    // Filter data based on status
    const filteredData = statusFilter
        ? data?.filter(odev => getHomeworkStatus(odev.teslimTarihi) === statusFilter)
        : data;

    // Render skeletons for better UX during loading
    const renderSkeletons = () => {
        if (isTablet) {
            return (
                <Grid container spacing={2}>
                    {[1, 2, 3].map((item) => (
                        <Grid item xs={12} sm={6} md={4} key={item}>
                            <Card sx={{ height: '100%', borderRadius: theme.shape.borderRadius * 1.5 }}>
                                <Box sx={{ p: 2, bgcolor: theme.palette.primary.main }}>
                                    <Skeleton variant="text" width="60%" height={24} sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />
                                </Box>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
                                        <Box>
                                            <Skeleton variant="text" width={120} />
                                            <Skeleton variant="text" width={80} />
                                        </Box>
                                    </Box>
                                    <Divider sx={{ my: 1.5 }} />
                                    <Box sx={{ mt: 2 }}>
                                        <Skeleton variant="text" width="100%" />
                                        <Skeleton variant="text" width="70%" />
                                    </Box>
                                </CardContent>
                                <Box sx={{ p: 1.5, bgcolor: theme.palette.grey[100] }}>
                                    <Skeleton variant="rectangular" height={32} width={100} />
                                </Box>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            );
        }

        return (
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            {['Ödev Tarihi', 'Teslim Tarihi', 'Öğretmen', 'Ders', 'Konu', 'Puan', 'Soru', 'Açıklama'].map((header) => (
                                <TableCell key={header} sx={{ backgroundColor: theme.palette.primary.main }}>
                                    <Skeleton variant="text" sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {[1, 2, 3, 4].map((row) => (
                            <TableRow key={row}>
                                {[1, 2, 3, 4, 5, 6, 7, 8].map((cell) => (
                                    <TableCell key={cell}>
                                        <Skeleton variant="text" />
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    // Render card view for mobile and tablet
    const renderCardView = () => {
        return (
            <Grid container spacing={2}>
                {filteredData?.map((odev, index) => {
                    const status = getHomeworkStatus(odev.teslimTarihi);
                    const statusText = {
                        active: 'Aktif',
                        due: 'Yaklaşıyor',
                        overdue: 'Gecikmiş'
                    };

                    const statusIcon = {
                        active: <DoneIcon />,
                        due: <WarningIcon />,
                        overdue: <ErrorOutlineIcon />
                    };

                    return (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                            <HomeworkCard>
                                <Box sx={{
                                    p: 2,
                                    bgcolor: theme.palette.primary.main,
                                    color: 'white',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <AssignmentIcon />
                                        <Typography variant="subtitle1" fontWeight="bold" noWrap>
                                            {odev.konu}
                                        </Typography>
                                    </Box>
                                    <Chip
                                        label={statusText[status]}
                                        size="small"
                                        icon={statusIcon[status]}
                                        sx={{
                                            bgcolor:
                                                status === 'active'
                                                    ? theme.palette.success.main
                                                    : status === 'due'
                                                        ? theme.palette.warning.main
                                                        : theme.palette.error.main,
                                            color: 'white',
                                            fontWeight: 'bold',
                                            '& .MuiChip-icon': {
                                                color: 'white'
                                            }
                                        }}
                                    />
                                </Box>

                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <Avatar
                                            sx={{
                                                bgcolor: theme.palette.secondary.main,
                                                mr: 2
                                            }}
                                        >
                                            <PersonIcon />
                                        </Avatar>
                                        <Box>
                                            <Typography variant="subtitle1" fontWeight="medium">
                                                {odev.ogretmen.trim()}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {odev.ders.trim()}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Divider sx={{ my: 1.5 }} />

                                    <Stack spacing={1.5} sx={{ mt: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <CalendarTodayIcon color="action" fontSize="small" />
                                            <Typography variant="body2">
                                                Verilme: {format(new Date(odev.odevTarihi), 'dd MMMM yyyy', { locale: tr })}
                                            </Typography>
                                        </Box>

                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <EventAvailableIcon
                                                fontSize="small"
                                                color={
                                                    status === 'active'
                                                        ? 'success'
                                                        : status === 'due'
                                                            ? 'warning'
                                                            : 'error'
                                                }
                                            />
                                            <Typography
                                                variant="body2"
                                                color={
                                                    status === 'active'
                                                        ? 'success.main'
                                                        : status === 'due'
                                                            ? 'warning.main'
                                                            : 'error.main'
                                                }
                                                fontWeight="medium"
                                            >
                                                Teslim: {format(new Date(odev.teslimTarihi), 'dd MMMM yyyy', { locale: tr })}
                                            </Typography>
                                        </Box>

                                        {odev.soru && (
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                                <HelpOutlineIcon color="action" fontSize="small" sx={{ mt: 0.5 }} />
                                                <Typography variant="body2">
                                                    Soru: {odev.soru}
                                                </Typography>
                                            </Box>
                                        )}

                                        {odev.aciklama && (
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                                <DescriptionIcon color="action" fontSize="small" sx={{ mt: 0.5 }} />
                                                <Typography variant="body2">
                                                    {odev.aciklama}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Stack>
                                </CardContent>

                                <Box sx={{
                                    p: 1.5,
                                    bgcolor: theme.palette.grey[100],
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}>
                                    <Chip
                                        label={`Puan: ${odev.puan || 'Belirtilmemiş'}`}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                        icon={<GradeIcon fontSize="small" />}
                                    />

                                    {status !== 'overdue' && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <AccessTimeIcon
                                                fontSize="small"
                                                color={status === 'due' ? 'warning' : 'action'}
                                            />
                                            <Typography
                                                variant="caption"
                                                color={status === 'due' ? 'warning.main' : 'text.secondary'}
                                                fontWeight={status === 'due' ? 'bold' : 'normal'}
                                            >
                                                {calculateRemainingTime(odev.teslimTarihi)}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </HomeworkCard>
                        </Grid>
                    );
                })}
            </Grid>
        );
    };

    // Calculate remaining time for homework
    const calculateRemainingTime = (teslimTarihi: Date): string => {
        const now = new Date();
        const dueDate = new Date(teslimTarihi);

        const diffTime = dueDate.getTime() - now.getTime();
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        const diffHours = Math.floor((diffTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

        if (diffDays > 0) {
            return `${diffDays} gün kaldı`;
        } else if (diffHours > 0) {
            return `${diffHours} saat kaldı`;
        } else {
            return 'Son gün!';
        }
    };

    // Render table view for desktop
    const renderTableView = () => {
        return (
            <TableContainer
                component={Paper}
                elevation={3}
                sx={{
                    minWidth: 800,
                    borderRadius: theme.shape.borderRadius * 1.5,
                    overflow: 'hidden'
                }}
            >
                <Table aria-label="ödevlerim tablosu" size="small">
                    <TableHead>
                        <TableRow>
                            <HeaderTableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <CalendarTodayIcon sx={{ mr: 1 }} />
                                    Ödev Tarihi
                                </Box>
                            </HeaderTableCell>
                            <HeaderTableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <EventAvailableIcon sx={{ mr: 1 }} />
                                    Teslim Tarihi
                                </Box>
                            </HeaderTableCell>
                            <HeaderTableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <PersonIcon sx={{ mr: 1 }} />
                                    Öğretmen
                                </Box>
                            </HeaderTableCell>
                            <HeaderTableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <SchoolIcon sx={{ mr: 1 }} />
                                    Ders
                                </Box>
                            </HeaderTableCell>
                            <HeaderTableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <SubjectIcon sx={{ mr: 1 }} />
                                    Konu
                                </Box>
                            </HeaderTableCell>
                            <HeaderTableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <GradeIcon sx={{ mr: 1 }} />
                                    Puan
                                </Box>
                            </HeaderTableCell>
                            <HeaderTableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <HelpOutlineIcon sx={{ mr: 1 }} />
                                    Soru
                                </Box>
                            </HeaderTableCell>
                            <HeaderTableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <DescriptionIcon sx={{ mr: 1 }} />
                                    Açıklama
                                </Box>
                            </HeaderTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredData?.map((odev, index) => {
                            const status = getHomeworkStatus(odev.teslimTarihi);

                            return (
                                <StyledTableRow key={index}>
                                    <StyledTableCell>
                                        <Chip
                                            label={format(new Date(odev.odevTarihi), 'dd MMMM yyyy', { locale: tr })}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                            icon={<CalendarTodayIcon />}
                                        />
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <Chip
                                            label={format(new Date(odev.teslimTarihi), 'dd MMMM yyyy', { locale: tr })}
                                            size="small"
                                            color={
                                                status === 'active'
                                                    ? 'success'
                                                    : status === 'due'
                                                        ? 'warning'
                                                        : 'error'
                                            }
                                            icon={
                                                status === 'active'
                                                    ? <DoneIcon />
                                                    : status === 'due'
                                                        ? <WarningIcon />
                                                        : <ErrorOutlineIcon />
                                            }
                                            sx={{
                                                '& .MuiChip-icon': {
                                                    color:
                                                        status === 'active'
                                                            ? theme.palette.success.main
                                                            : status === 'due'
                                                                ? theme.palette.warning.main
                                                                : theme.palette.error.main
                                                }
                                            }}
                                        />
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Avatar
                                                sx={{
                                                    width: 24,
                                                    height: 24,
                                                    bgcolor: theme.palette.secondary.main,
                                                    fontSize: '0.75rem'
                                                }}
                                            >
                                                {odev.ogretmen.trim().charAt(0)}
                                            </Avatar>
                                            <Typography variant="body2">
                                                {odev.ogretmen.trim()}
                                            </Typography>
                                        </Box>
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <Chip
                                            label={odev.ders.trim()}
                                            size="small"
                                            color="default"
                                            variant="outlined"
                                            icon={<SchoolIcon fontSize="small" />}
                                            sx={{
                                                bgcolor: theme.palette.background.paper,
                                                '& .MuiChip-icon': {
                                                    color: theme.palette.primary.main,
                                                }
                                            }}
                                        />
                                    </StyledTableCell>
                                    <TextOverflowTableCell>
                                        <Tooltip title={odev.konu} placement="top" arrow>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    display: 'block',
                                                    width: '100%',
                                                    fontSize: '0.75rem',
                                                    whiteSpace: 'normal',
                                                    wordBreak: 'break-word'
                                                }}
                                            >
                                                {odev.konu}
                                            </Typography>
                                        </Tooltip>
                                    </TextOverflowTableCell>
                                    <StyledTableCell align="center">
                                        <Chip
                                            label={odev.puan || 'Belirtilmemiş'}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                        />
                                    </StyledTableCell>
                                    <TextOverflowTableCell>
                                        <Tooltip title={odev.soru || '-'} placement="top" arrow>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    display: 'block',
                                                    width: '100%',
                                                    fontSize: '0.75rem',
                                                    whiteSpace: 'normal',
                                                    wordBreak: 'break-word'
                                                }}
                                            >
                                                {odev.soru || '-'}
                                            </Typography>
                                        </Tooltip>
                                    </TextOverflowTableCell>
                                    <TextOverflowTableCell>
                                        <Tooltip title={odev.aciklama || '-'} placement="top" arrow>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    display: 'block',
                                                    width: '100%',
                                                    fontSize: '0.75rem',
                                                    whiteSpace: 'normal',
                                                    wordBreak: 'break-word'
                                                }}
                                            >
                                                {odev.aciklama || '-'}
                                            </Typography>
                                        </Tooltip>
                                    </TextOverflowTableCell>
                                </StyledTableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    // Calculate statistics
    const calculateStats = () => {
        if (!data) return { total: 0, active: 0, due: 0, overdue: 0 };

        const active = data.filter(odev => getHomeworkStatus(odev.teslimTarihi) === 'active').length;
        const due = data.filter(odev => getHomeworkStatus(odev.teslimTarihi) === 'due').length;
        const overdue = data.filter(odev => getHomeworkStatus(odev.teslimTarihi) === 'overdue').length;

        return {
            total: data.length,
            active,
            due,
            overdue
        };
    };

    const stats = calculateStats();

    return (
        <Box sx={{ px: { xs: 1, sm: 2 } }}>
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                mb: 3
            }}>
                <Typography
                    variant="h5"
                    component="h1"
                    sx={{
                        fontWeight: 600,
                        mb: { xs: 2, sm: 0 },
                        color: theme.palette.text.primary
                    }}
                >
                    <AssignmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Ödevlerim
                </Typography>

                <SeasonSelector
                    variant="outlined"
                    size="small"
                    sx={{ minWidth: 200 }}
                >
                    <InputLabel id="season-select-label">Eğitim Sezonu</InputLabel>
                    <Select
                        labelId="season-select-label"
                        id="season-select"
                        value={selectedSeason}
                        onChange={handleSeasonChange}
                        label="Eğitim Sezonu"
                    >
                        {availableSeasons.map((season) => (
                            <MenuItem key={season} value={season}>
                                {season} - {(parseInt(season) + 1)} Eğitim Yılı
                            </MenuItem>
                        ))}
                    </Select>
                </SeasonSelector>
            </Box>

            {/* Statistics Cards - Only show when data is available */}
            {!isLoading && !error && data && data?.length > 0 && (
                <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} lg={3}>
                        <Card elevation={3} sx={{
                            height: '100%',
                            transition: 'transform 0.3s, box-shadow 0.3s',
                            '&:hover': {
                                transform: 'translateY(-5px)',
                                boxShadow: theme.shadows[6],
                            },
                        }}>
                            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                                    <AssignmentIcon />
                                </Avatar>
                                <Box>
                                    <Typography variant="h4" fontWeight="bold">{stats.total}</Typography>
                                    <Typography variant="body2" color="text.secondary">Toplam Ödev</Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} lg={3}>
                        <Card elevation={3} sx={{
                            height: '100%',
                            transition: 'transform 0.3s, box-shadow 0.3s',
                            '&:hover': {
                                transform: 'translateY(-5px)',
                                boxShadow: theme.shadows[6],
                            },
                        }}>
                            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ bgcolor: theme.palette.success.main }}>
                                    <DoneIcon />
                                </Avatar>
                                <Box>
                                    <Typography variant="h4" fontWeight="bold">{stats.active}</Typography>
                                    <Typography variant="body2" color="text.secondary">Aktif Ödev</Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} lg={3}>
                        <Card elevation={3} sx={{
                            height: '100%',
                            transition: 'transform 0.3s, box-shadow 0.3s',
                            '&:hover': {
                                transform: 'translateY(-5px)',
                                boxShadow: theme.shadows[6],
                            },
                        }}>
                            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ bgcolor: theme.palette.warning.main }}>
                                    <WarningIcon />
                                </Avatar>
                                <Box>
                                    <Typography variant="h4" fontWeight="bold">{stats.due}</Typography>
                                    <Typography variant="body2" color="text.secondary">Yaklaşan Ödev</Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} lg={3}>
                        <Card elevation={3} sx={{
                            height: '100%',
                            transition: 'transform 0.3s, box-shadow 0.3s',
                            '&:hover': {
                                transform: 'translateY(-5px)',
                                boxShadow: theme.shadows[6],
                            },
                        }}>
                            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ bgcolor: theme.palette.error.main }}>
                                    <ErrorOutlineIcon />
                                </Avatar>
                                <Box>
                                    <Typography variant="h4" fontWeight="bold">{stats.overdue}</Typography>
                                    <Typography variant="body2" color="text.secondary">Gecikmiş Ödev</Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {/* Filter chips - Only show when data is available */}
            {!isLoading && !error && data && data?.length > 0 && (
                <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                        <FilterListIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                        Filtrele:
                    </Typography>

                    <Chip
                        label="Tümü"
                        color={statusFilter === null ? "primary" : "default"}
                        variant={statusFilter === null ? "filled" : "outlined"}
                        onClick={() => setStatusFilter(null)}
                        sx={{ fontWeight: statusFilter === null ? 'bold' : 'normal' }}
                    />

                    <Chip
                        label="Aktif"
                        color={statusFilter === 'active' ? "success" : "default"}
                        variant={statusFilter === 'active' ? "filled" : "outlined"}
                        onClick={() => setStatusFilter('active')}
                        icon={<DoneIcon />}
                        sx={{ fontWeight: statusFilter === 'active' ? 'bold' : 'normal' }}
                    />

                    <Chip
                        label="Yaklaşan"
                        color={statusFilter === 'due' ? "warning" : "default"}
                        variant={statusFilter === 'due' ? "filled" : "outlined"}
                        onClick={() => setStatusFilter('due')}
                        icon={<WarningIcon />}
                        sx={{ fontWeight: statusFilter === 'due' ? 'bold' : 'normal' }}
                    />

                    <Chip
                        label="Gecikmiş"
                        color={statusFilter === 'overdue' ? "error" : "default"}
                        variant={statusFilter === 'overdue' ? "filled" : "outlined"}
                        onClick={() => setStatusFilter('overdue')}
                        icon={<ErrorOutlineIcon />}
                        sx={{ fontWeight: statusFilter === 'overdue' ? 'bold' : 'normal' }}
                    />
                </Box>
            )}

            {isLoading ? (
                <Box sx={{ mt: 3 }}>
                    {renderSkeletons()}
                </Box>
            ) : error ? (
                <Alert
                    severity="error"
                    variant="filled"
                    sx={{
                        mt: 2,
                        borderRadius: theme.shape.borderRadius * 1.5,
                        boxShadow: theme.shadows[3]
                    }}
                    action={
                        process.env.NODE_ENV === 'development' && (
                            <Typography variant="caption" display="block" sx={{ color: 'white' }}>
                                CorrelationId: {(error as OdevlerimError).correlationId}
                            </Typography>
                        )
                    }
                >
                    {(error as Error).message}
                </Alert>
            ) : !data?.length ? (
                <Alert
                    severity="info"
                    variant="outlined"
                    icon={<AssignmentIcon />}
                    sx={{
                        mt: 2,
                        borderRadius: theme.shape.borderRadius * 1.5,
                        boxShadow: theme.shadows[1],
                        bgcolor: theme.palette.background.paper
                    }}
                >
                    <Typography variant="body1">
                        {selectedSeason} - {parseInt(selectedSeason) + 1} eğitim sezonunda kayıtlı ödev bulunmamaktadır.
                    </Typography>
                </Alert>
            ) : filteredData?.length === 0 ? (
                <Alert
                    severity="info"
                    variant="outlined"
                    icon={<FilterListIcon />}
                    sx={{
                        mt: 2,
                        borderRadius: theme.shape.borderRadius * 1.5,
                        boxShadow: theme.shadows[1],
                        bgcolor: theme.palette.background.paper
                    }}
                >
                    <Typography variant="body1">
                        Seçilen filtreye uygun ödev bulunmamaktadır.
                    </Typography>
                </Alert>
            ) : (
                <Box sx={{ mt: { xs: 2, sm: 3 } }}>
                    {isTablet ? renderCardView() : renderTableView()}
                </Box>
            )}
        </Box>
    );
};

export default Odevlerim;